import React, {ButtonHTMLAttributes, ReactNode} from 'react';
import './CLButton.scss';
import classNames from 'classnames';
import CLMaskIcon from '../CLMaskIcon/CLMaskIcon';
import CLSpinner from '../CLSpinner/CLSpinner';
import {AutomationProps} from 'common/src/modules/automation/automation.types';
import addAutomationTag from 'common/src/modules/automation/utils/addAutomationTag';
import CLOverlayTrigger from '../CLOverlayTrigger/CLOverlayTrigger';

export interface CLButtonProps extends AutomationProps, ButtonHTMLAttributes<HTMLButtonElement> {
  /* What background color to use
   */
  buttonType?: 'primary' | 'secondary' | 'regular';
  /**
   * How large should the button be?
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * should the button take all the width?
   * default: false
   */
  fullWidth?: boolean;
  /**
   * Button contents
   */
  label: ReactNode;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * should the button be disabled
   */
  isDisabled?: boolean;
  /**
   * wrapper class for the button
   */
  className?: string;
  icon?: {
    src: string;
    className?: string;
  };
  suppressPropagation?: boolean;
  /**
   * Loading state for the button
   */
  isExecuting?: boolean;
  /**
   * Should show unsaved indicator or not
   */
  unsavedIndicator?: boolean;
  /**
   * Tooltip text
   */
  tooltip?: string;
}

const CLButton = React.forwardRef<HTMLButtonElement, CLButtonProps>(
  (
    {
      size = 'medium',
      buttonType = 'primary',
      label,
      isDisabled,
      fullWidth = false,
      className,
      icon,
      style,
      suppressPropagation,
      onClick,
      isExecuting,
      unsavedIndicator,
      qaHook = '',
      tooltip = '',
      ...props
    }: CLButtonProps,
    ref,
  ) => {
    style = style || {};

    if (icon) {
      style.display = 'flex';
    }

    return (
      <CLOverlayTrigger trigger="hover" popper={tooltip} >
        <button
          {...addAutomationTag(qaHook)}
          style={style}
          ref={ref}
          type="button"
          disabled={isDisabled || isExecuting}
          className={classNames('common_clinch_button', size, buttonType, className, {
            full_width: fullWidth,
          })}
          onClick={(e) => {
            suppressPropagation && e.stopPropagation();

            if (!isExecuting && onClick) {
              onClick();
            }
          }}
          {...props}
        >
          {icon && (
            <CLMaskIcon src={icon.src} className={classNames('button_icon', icon.className)} />
          )}
          {isExecuting ? (
            <CLSpinner
              spinnerClassName={classNames('indication_spinner', buttonType || 'primary')}
              message={label}
            />
          ) : (
            label
          )}
          {unsavedIndicator && <div className="unsaved_indicator" />}
        </button>
      </CLOverlayTrigger>
    );
  },
);

export default CLButton;
